import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const PLAN_LABELS = {
  starter: 'Starter',
  commander: 'Commander',
  operator: 'Operator',
  elite: 'Elite',
};

const FIRST_MISSION_PROMPT =
  'Use my business context and build my first 7-day execution plan. Include my highest-priority objective, target customer, offer angle, outreach assets, content moves, and the first three actions I should take.';

export default function PaymentSuccessOnboarding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const tierParam = searchParams.get('tier') || '';
  const planName = PLAN_LABELS[tierParam.toLowerCase()] || null;

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const goToCommandCenter = () => navigate('/command-center-ai-execution-interface');

  const runFirstMission = () =>
    navigate('/command-center-ai-execution-interface', {
      state: { prefillMission: FIRST_MISSION_PROMPT },
    });

  const headline = planName ? `Welcome to RecallCore ${planName}` : 'Welcome to RecallCore';

  const subheadline = planName
    ? `Your ${planName} workspace is live. Start with one guided mission and turn your business context into execution-ready assets.`
    : 'Your workspace is live. Start with one guided mission and turn your business context into execution-ready assets.';

  const planBadge = planName ? `${planName.toUpperCase()} WORKSPACE ACTIVE` : 'WORKSPACE ACTIVE';

  const card2Title = planName ? `${planName} Access Active` : 'Access Active';

  const card2Body = planName
    ? 'Your upgraded mission access is connected to this account.'
    : 'Your mission access is connected to this account.';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#05060f',
        color: '#e8eaf0',
        fontFamily: "'Rajdhani', 'Share Tech Mono', sans-serif",
        overflowX: 'hidden',
        position: 'relative',
        isolation: 'isolate',
      }}
    >
      <style>{`
        @keyframes pso-star-twinkle {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 0.75; }
        }

        .pso-card {
          transition: border-color 0.25s ease, transform 0.25s ease, box-shadow 0.3s ease, background 0.25s ease;
        }

        .pso-card:hover {
          border-color: rgba(45,200,150,0.5);
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(45,200,150,0.12), inset 0 0 0 1px rgba(45,200,150,0.25);
          background: rgba(14,20,28,0.72);
        }

        .pso-btn-primary {
          transition: transform 0.15s ease, box-shadow 0.25s ease, background 0.2s ease;
        }

        .pso-btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 0 22px rgba(45,200,150,0.45), 0 6px 24px rgba(45,200,150,0.28);
          background: #37e0aa;
        }

        .pso-btn-command {
          transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
        }

        .pso-btn-command:hover {
          border-color: #FFD700;
          background: rgba(255,215,0,0.08);
          color: #FFD700;
          box-shadow: 0 0 18px rgba(255,215,0,0.18);
        }

        .pso-btn-outline {
          transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
        }

        .pso-btn-outline:hover {
          border-color: #2DC896;
          background: rgba(45,200,150,0.08);
          color: #ffffff;
        }

        @media (max-width: 720px) {
          .pso-hero {
            padding: 36px 22px 30px !important;
          }

          .pso-headline {
            font-size: clamp(26px, 7vw, 34px) !important;
          }
        }
      `}</style>

      {/* Background from first code: animated starfield */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        {Array.from({ length: 70 }).map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${(i * 37 + 13) % 100}%`,
              top: `${(i * 53 + 7) % 100}%`,
              width: i % 3 === 0 ? 2 : 1,
              height: i % 3 === 0 ? 2 : 1,
              borderRadius: '50%',
              background: i % 5 === 0 ? '#2DC896' : '#ffffff',
              opacity: 0.2,
              animation: `pso-star-twinkle ${2 + (i % 4)}s ease-in-out infinite`,
              animationDelay: `${(i * 0.3) % 3}s`,
            }}
          />
        ))}
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: '880px',
          margin: '0 auto',
          padding: '64px 24px 80px',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(18px)',
          transition: 'opacity 0.55s ease, transform 0.55s ease',
        }}
      >
        {/* Hero */}
        <div
          className="pso-hero"
          style={{
            position: 'relative',
            borderRadius: '18px',
            padding: '52px 44px 40px',
            marginBottom: '28px',
            background: 'rgba(8,11,18,0.72)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(45,200,150,0.26)',
            boxShadow:
              '0 30px 80px rgba(0,0,0,0.5), 0 0 48px rgba(45,200,150,0.07), inset 0 1px 0 rgba(255,255,255,0.04)',
            textAlign: 'center',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: '60px',
              height: '60px',
              margin: '0 auto 22px',
              border: '1px solid rgba(45,200,150,0.55)',
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 0 4px rgba(45,200,150,0.06), 0 0 28px rgba(45,200,150,0.22)',
              background: 'rgba(45,200,150,0.08)',
            }}
          >
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
              <polyline
                points="6,17 13,24 26,9"
                stroke="#2DC896"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div
            style={{
              display: 'inline-block',
              background: 'rgba(45,200,150,0.1)',
              border: '1px solid rgba(45,200,150,0.32)',
              borderRadius: '4px',
              padding: '5px 14px',
              fontSize: '11px',
              letterSpacing: '2.5px',
              color: '#2DC896',
              marginBottom: '20px',
              fontFamily: "'Share Tech Mono', monospace",
            }}
          >
            PAYMENT CONFIRMED
          </div>

          <h1
            className="pso-headline"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              fontSize: 'clamp(28px, 5vw, 42px)',
              fontWeight: 700,
              color: '#ffffff',
              margin: '0 0 14px',
              lineHeight: 1.18,
              letterSpacing: '-0.3px',
            }}
          >
            {headline}
          </h1>

          <div
            style={{
              display: 'inline-block',
              background: 'rgba(45,200,150,0.08)',
              border: '1px solid rgba(45,200,150,0.32)',
              borderRadius: '3px',
              padding: '5px 12px',
              fontSize: '10.5px',
              letterSpacing: '3px',
              color: '#2DC896',
              marginBottom: '18px',
              fontFamily: "'Share Tech Mono', monospace",
            }}
          >
            {planBadge}
          </div>

          <p
            style={{
              fontSize: '16px',
              color: 'rgba(232,234,240,0.72)',
              margin: '0 auto 28px',
              lineHeight: 1.6,
              maxWidth: '600px',
            }}
          >
            {subheadline}
          </p>

          <div
            style={{
              display: 'flex',
              gap: '14px',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginBottom: '24px',
            }}
          >
            <button
              className="pso-btn-primary"
              onClick={runFirstMission}
              style={{
                background: '#2DC896',
                color: '#04140E',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '8px',
                padding: '14px 26px',
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '1.5px',
                fontFamily: "'Orbitron', sans-serif",
                cursor: 'pointer',
                boxShadow: '0 4px 18px rgba(45,200,150,0.28)',
              }}
            >
              RUN YOUR FIRST MISSION
            </button>

            <button
              className="pso-btn-command"
              onClick={goToCommandCenter}
              style={{
                background: 'transparent',
                color: '#FFD700',
                border: '1px solid rgba(255,215,0,0.45)',
                borderRadius: '8px',
                padding: '14px 24px',
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '1.5px',
                fontFamily: "'Orbitron', sans-serif",
                cursor: 'pointer',
              }}
            >
              OPEN COMMAND CENTER
            </button>
          </div>

          <div
            style={{
              maxWidth: '620px',
              margin: '0 auto',
              paddingTop: '18px',
              borderTop: '1px solid rgba(45,200,150,0.14)',
              color: 'rgba(232,234,240,0.6)',
              fontSize: '14px',
              lineHeight: 1.55,
            }}
          >
            You&rsquo;re in. RecallCore is ready to help you move from idea to execution without starting from a blank page.
          </div>
        </div>

        {/* Activation cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '14px',
            marginBottom: '28px',
          }}
        >
          {[
            {
              num: '01',
              title: 'Payment Verified',
              body: 'Your checkout was completed securely.',
            },
            {
              num: '02',
              title: card2Title,
              body: card2Body,
            },
            {
              num: '03',
              title: 'Workspace Ready',
              body: 'You can now run missions, generate assets, and move work into action.',
            },
          ].map((card, i) => (
            <div
              key={i}
              className="pso-card"
              style={{
                background: 'rgba(10,14,22,0.62)',
                border: '1px solid rgba(45,200,150,0.18)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                borderRadius: '12px',
                padding: '22px 22px 24px',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(12px)',
                transition: `opacity 0.5s ease ${0.15 + i * 0.1}s, transform 0.5s ease ${
                  0.15 + i * 0.1
                }s, border-color 0.25s ease, box-shadow 0.3s ease, background 0.25s ease`,
              }}
            >
              <div
                style={{
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: '11px',
                  color: '#2DC896',
                  marginBottom: '10px',
                  letterSpacing: '2px',
                }}
              >
                {card.num}
              </div>

              <div
                style={{
                  fontFamily: "'Orbitron', sans-serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#ffffff',
                  marginBottom: '8px',
                  letterSpacing: '0.3px',
                }}
              >
                {card.title}
              </div>

              <div
                style={{
                  fontSize: '14px',
                  color: 'rgba(232,234,240,0.6)',
                  lineHeight: 1.55,
                }}
              >
                {card.body}
              </div>
            </div>
          ))}
        </div>

        {/* First move */}
        <div
          style={{
            background: 'rgba(10,14,22,0.62)',
            border: '1px solid rgba(45,200,150,0.32)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: '14px',
            padding: '28px 28px 26px',
            marginBottom: '28px',
          }}
        >
          <div
            style={{
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '11px',
              letterSpacing: '2.8px',
              color: '#2DC896',
              marginBottom: '10px',
            }}
          >
            YOUR FIRST MOVE
          </div>

          <p
            style={{
              fontSize: '15px',
              color: 'rgba(232,234,240,0.72)',
              lineHeight: 1.55,
              margin: '0 0 18px',
            }}
          >
            Start here if you want RecallCore to create momentum immediately.
          </p>

          <div
            style={{
              position: 'relative',
              background: 'rgba(8,11,18,0.6)',
              border: '1px solid rgba(45,200,150,0.14)',
              borderLeft: '2px solid #2DC896',
              borderRadius: '8px',
              padding: '18px 20px',
              marginBottom: '20px',
            }}
          >
            <p
              style={{
                fontSize: '15px',
                color: 'rgba(232,234,240,0.85)',
                lineHeight: 1.65,
                margin: 0,
                fontStyle: 'italic',
              }}
            >
              {FIRST_MISSION_PROMPT}
            </p>
          </div>

          <button
            className="pso-btn-outline"
            onClick={runFirstMission}
            style={{
              background: 'transparent',
              border: '1px solid rgba(45,200,150,0.55)',
              color: '#2DC896',
              borderRadius: '8px',
              padding: '12px 22px',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '2px',
              fontFamily: "'Orbitron', sans-serif",
              cursor: 'pointer',
            }}
          >
            RUN THIS MISSION
          </button>
        </div>

        {/* What happens next - details from first code */}
        <div
          style={{
            background: 'rgba(10,14,22,0.5)',
            border: '1px solid rgba(45,200,150,0.16)',
            borderRadius: '14px',
            padding: '24px 28px 22px',
            marginBottom: '36px',
          }}
        >
          <div
            style={{
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '11px',
              letterSpacing: '2.8px',
              color: '#2DC896',
              marginBottom: '6px',
            }}
          >
            WHAT HAPPENS NEXT
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {[
              'Add your business context',
              'Run your first mission',
              'Save your best output as an asset',
              'Turn execution into leads, campaigns, or delivery work',
            ].map((step, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '14px 0',
                  borderBottom: i < 3 ? '1px solid rgba(45,200,150,0.08)' : 'none',
                }}
              >
                <div
                  style={{
                    width: '26px',
                    height: '26px',
                    minWidth: '26px',
                    borderRadius: '50%',
                    border: '1px solid rgba(45,200,150,0.32)',
                    background: 'rgba(45,200,150,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: "'Share Tech Mono', monospace",
                    fontSize: '12px',
                    color: '#2DC896',
                    fontWeight: 600,
                  }}
                >
                  {i + 1}
                </div>

                <span
                  style={{
                    fontSize: '15px',
                    color: 'rgba(232,234,240,0.78)',
                    lineHeight: 1.5,
                  }}
                >
                  {step}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            textAlign: 'center',
            fontSize: '13px',
            color: 'rgba(232,234,240,0.35)',
          }}
        >
          <span>Need help? </span>
          <a
            href="mailto:support@recallcore.ai"
            style={{
              color: '#2DC896',
              textDecoration: 'none',
              fontFamily: "'Share Tech Mono', monospace",
            }}
          >
            support@recallcore.ai
          </a>
        </div>
      </div>
    </div>
  );
}